// SPDX-License-Identifier: AGPL-3.0-or-later

import {useAnimatedImageUrl} from '@app/features/app/hooks/useAnimatedImageUrl';
import {GifIndicator} from '@app/features/channel/components/embeds/media/GifIndicator';
import type {AvatarDecorationSurface} from '@app/features/cosmetics/components/AvatarUltraLayer';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/profile/profile_card/ProfileCardBanner.module.css';
import {
	getProfileAvatarDecorationBounds,
	getProfileAvatarLeft,
	PROFILE_POPOUT_GEOMETRY,
} from '@app/features/user/constants/UserProfileSurfaceGeometry';
import type {User} from '@app/features/user/models/User';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

interface ProfileCardBannerProps {
	bannerUrl: string | null;
	hoverBannerUrl?: string | null;
	bannerColor: string;
	user: User;
	avatarUrl: string | null;
	hoverAvatarUrl: string | null;
	disablePresence?: boolean;
	isClickable?: boolean;
	onAvatarClick?: () => void;
	onAvatarContextMenu?: (event: React.MouseEvent) => void;
	onBannerContextMenu?: (event: React.MouseEvent) => void;
	headerHeight?: number;
	decorationSurface?: AvatarDecorationSurface;
}

// Micup: the card shows above the avatar; a banner image fades into it and a soft light in the profile's colour sits
// behind the avatar.
export const ProfileCardBanner: React.FC<ProfileCardBannerProps> = observer(
	({
		bannerUrl,
		hoverBannerUrl,
		bannerColor,
		user,
		avatarUrl,
		hoverAvatarUrl,
		disablePresence = false,
		isClickable = true,
		onAvatarClick,
		onAvatarContextMenu,
		onBannerContextMenu,
		headerHeight = PROFILE_POPOUT_GEOMETRY.headerHeightPx,
		decorationSurface = 'profile',
	}) => {
		const bannerHeight = PROFILE_POPOUT_GEOMETRY.bannerHeightPx;
		const hasProfileUltra = decorationSurface === 'profile' && Boolean(user.avatarUltra);
		const bounds =
			decorationSurface === 'profile' && !hasProfileUltra
				? getProfileAvatarDecorationBounds(
						user,
						PROFILE_POPOUT_GEOMETRY.avatarSizePx,
						PROFILE_POPOUT_GEOMETRY.avatarBorderPx,
					)
				: null;
		const avatarLeft = bounds
			? getProfileAvatarLeft({
					decorationLeft: bounds.left,
					size: PROFILE_POPOUT_GEOMETRY.avatarSizePx,
					border: PROFILE_POPOUT_GEOMETRY.avatarBorderPx,
					edgePadding: PROFILE_POPOUT_GEOMETRY.avatarLeftPx,
					containerWidth: PROFILE_POPOUT_GEOMETRY.contentWidthPx,
				})
			: PROFILE_POPOUT_GEOMETRY.avatarLeftPx;
		const avatarTop = bounds
			? Math.max(PROFILE_POPOUT_GEOMETRY.avatarTopPx, -PROFILE_POPOUT_GEOMETRY.avatarBorderPx - bounds.top)
			: PROFILE_POPOUT_GEOMETRY.avatarTopPx;
		const avatarCentre = PROFILE_POPOUT_GEOMETRY.avatarBorderPx + PROFILE_POPOUT_GEOMETRY.avatarSizePx / 2;
		const headerStyle = {
			'--profile-card-glow-x': remFromPx(avatarLeft + avatarCentre),
			'--profile-card-glow-y': remFromPx(avatarTop + avatarCentre),
			'--profile-card-accent': bannerColor,
			height: remFromPx(
				hasProfileUltra
					? headerHeight
					: Math.max(
							headerHeight,
							avatarTop +
								PROFILE_POPOUT_GEOMETRY.avatarBorderPx +
								(bounds?.bottom ?? PROFILE_POPOUT_GEOMETRY.avatarSizePx),
						),
			),
		} as React.CSSProperties;
		const avatarStyle: React.CSSProperties = {left: remFromPx(avatarLeft), top: remFromPx(avatarTop)};
		const bannerWrapperStyle = useMemo<React.CSSProperties>(() => ({height: remFromPx(bannerHeight)}), [bannerHeight]);
		const {
			hoverRef: bannerHoverRef,
			imageUrl: activeBannerUrl,
			showGifIndicator,
		} = useAnimatedImageUrl({
			staticUrl: bannerUrl,
			animatedUrl: hoverBannerUrl,
			kind: 'gif',
		});
		const bannerStyle = useMemo<React.CSSProperties>(
			() => (activeBannerUrl ? {backgroundImage: `url(${activeBannerUrl})`} : {}),
			[activeBannerUrl],
		);
		return (
			<header
				className={styles.headerSection}
				style={headerStyle}
				data-flx="user.profile.profile-card.profile-card-banner.header-section"
			>
				<div
					ref={bannerHoverRef}
					role="group"
					className={styles.bannerWrapper}
					style={bannerWrapperStyle}
					onContextMenu={onBannerContextMenu}
					data-flx="user.profile.profile-card.profile-card-banner.banner-wrapper.banner-context-menu"
				>
					<div
						className={styles.banner}
						style={bannerStyle}
						data-flx="user.profile.profile-card.profile-card-banner.banner"
					/>
					{showGifIndicator && <GifIndicator data-flx="user.profile.profile-card.profile-card-banner.gif-indicator" />}
				</div>
				<FocusRing offset={-2} data-flx="user.profile.profile-card.profile-card-banner.focus-ring">
					<button
						type="button"
						onClick={onAvatarClick}
						onContextMenu={onAvatarContextMenu}
						className={styles.avatarButton}
						style={avatarStyle}
						data-flx="user.profile.profile-card.profile-card-banner.avatar-button.avatar-click"
					>
						<StatusAwareAvatar
							size={PROFILE_POPOUT_GEOMETRY.avatarSizePx}
							user={user}
							decorationSurface={decorationSurface}
							avatarUrl={avatarUrl}
							hoverAvatarUrl={hoverAvatarUrl}
							disablePresence={disablePresence}
							isClickable={isClickable}
							forceAnimate
							forceAnimateIgnoringSettings
							data-flx="user.profile.profile-card.profile-card-banner.status-aware-avatar"
						/>
					</button>
				</FocusRing>
			</header>
		);
	},
);
